declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

export type GAIdentifiers = {
  clientId?: string;
  sessionId?: string;
  sessionNumber?: string;
};

const PROD_HOST = 'app.dentistry.one';
const STAGING_HOST = 'qa.dentistry.one';

const PROD_SESSION_COOKIE = '_ga_JY8VXH32S8';
const STAGING_SESSION_COOKIE = '_ga_3MKRP680WL';

const MAIN_GA_COOKIE = '_ga';

const escapeRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getCookie = (name: string): string | undefined => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${escapeRegex(name)}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
};

const getHostname = (): string => window.location.hostname.toLowerCase();

const getRequiredSessionCookieName = (): string | undefined => {
  const hostname = getHostname();

  if (hostname === PROD_HOST) {
    return PROD_SESSION_COOKIE;
  }

  if (hostname === STAGING_HOST) {
    return STAGING_SESSION_COOKIE;
  }

  return undefined;
};

const getFallbackSessionCookieName = (): string | undefined => {
  const cookiePairs = document.cookie.split(';').map((part) => part.trim());
  const gaSessionCookie = cookiePairs.find((cookie) => /^_ga_[A-Z0-9]+=/.test(cookie));

  return gaSessionCookie?.split('=')[0];
};

export const getSessionCookieName = (): string | undefined => {
  return getRequiredSessionCookieName() ?? getFallbackSessionCookieName();
};

const parseClientIdFromGaCookie = (cookieValue?: string): string | undefined => {
  if (!cookieValue) return undefined;

  const parts = cookieValue.split('.');
  if (parts.length < 4) return undefined;

  const lastTwo = parts.slice(-2);
  if (lastTwo.some((part) => !part)) return undefined;

  return `${lastTwo[0]}.${lastTwo[1]}`;
};

/**
 * GA4 session cookies may contain tokenized fields such as:
 * - s<digits> => session id
 * - o<digits> => session number
 *
 * Example observed format:
 *   GS1.1.s1741890672$o3$g1$t1741899999...
 *
 * We intentionally extract these tokens by pattern instead of relying on fixed
 * positions, because the cookie may include additional segments over time.
 */
const parseSessionFieldsFromGaCookie = (
  cookieValue?: string,
): Pick<GAIdentifiers, 'sessionId' | 'sessionNumber'> => {
  if (!cookieValue) {
    return {};
  }

  const sessionId = cookieValue.match(/(?:^|[$.])s(\d+)/)?.[1];
  const sessionNumber = cookieValue.match(/(?:^|[$.])o(\d+)/)?.[1];

  return {
    sessionId,
    sessionNumber,
  };
};

const getClientId = (): string | undefined => {
  return parseClientIdFromGaCookie(getCookie(MAIN_GA_COOKIE));
};

const getSessionIdentifiers = (): Pick<GAIdentifiers, 'sessionId' | 'sessionNumber'> => {
  const sessionCookieName = getSessionCookieName();
  const sessionCookieValue = sessionCookieName ? getCookie(sessionCookieName) : undefined;

  return parseSessionFieldsFromGaCookie(sessionCookieValue);
};

export const getGAIdentifiers = (): GAIdentifiers => {
  const clientId = getClientId();
  const { sessionId, sessionNumber } = getSessionIdentifiers();

  return {
    clientId,
    sessionId,
    sessionNumber,
  };
};

export const getGACookieFingerprint = (): string => {
  const mainGaCookie = getCookie(MAIN_GA_COOKIE) ?? '';
  const sessionCookieName = getSessionCookieName() ?? '';
  const sessionCookieValue = sessionCookieName ? getCookie(sessionCookieName) ?? '' : '';

  return `${mainGaCookie}__${sessionCookieName}__${sessionCookieValue}`;
};
